/**
 * Firebase→Supabase JWT bridge — frontend token manager (multi-owner Phase 7).
 *
 * Fetches short-lived Supabase-signed JWTs from the getSupabaseToken callable
 * and caches them. Wired into the supabase client's `accessToken` option in
 * src/utils/supabase.js, but ONLY when the build sets
 * REACT_APP_SUPABASE_RLS_BRIDGE=true — until then the app keeps pure anon-key
 * behavior and this module is never invoked.
 *
 * Failure policy (changed after the 2026-11-03 Damian incident):
 *   • Signed OUT → null (anon key). Public surfaces keep working.
 *   • Signed IN + mint fails + a still-valid cached token exists → cached token.
 *   • Signed IN + mint fails + nothing cached → return a SENTINEL string.
 *     PostgREST rejects it with 401, so every query surfaces an explicit
 *     error instead of RLS silently filtering the book down to zero rows.
 *     A silent anon fallback here made an auth outage look like an EMPTY CRM
 *     ("my deals are gone") — an error state is honest and diagnosable.
 *   • While degraded: a `supabase-bridge-degraded` window event fires (the
 *     app shell shows a banner) and a background retry loop re-mints every
 *     RETRY_INTERVAL_MS until it succeeds, then fires
 *     `supabase-bridge-recovered`.
 *
 * NOTE: firebase modules are imported lazily inside the functions — importing
 * ./init-firebase at module scope breaks any jest suite that transitively
 * imports this file (Firebase Auth initializes at import time).
 */

const REFRESH_MARGIN_MS = 15 * 60 * 1000; // refresh when <15 min of life left
const MINT_COOLDOWN_MS = 5 * 1000;        // don't hammer the callable between failures
const RETRY_INTERVAL_MS = 30 * 1000;      // background re-mint cadence while degraded

// Deliberately not a JWT: PostgREST answers 401 "invalid token", making the
// outage visible in every query error and in request logs.
export const BRIDGE_DEGRADED_SENTINEL = 'bridge-token-unavailable';

export const BRIDGE_DEGRADED_EVENT = 'supabase-bridge-degraded';
export const BRIDGE_RECOVERED_EVENT = 'supabase-bridge-recovered';

let cached = { token: null, expiresAt: 0 };
let inflight = null;
let degraded = false;
let lastFailureAt = 0;
let retryTimer = null;

export function isRlsBridgeEnabled() {
  return process.env.REACT_APP_SUPABASE_RLS_BRIDGE === 'true';
}

function emit(eventName) {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(eventName));
    }
  } catch {
    /* never let banner plumbing break data access */
  }
}

function enterDegraded(err) {
  lastFailureAt = Date.now();
  // eslint-disable-next-line no-console
  console.error('[supabaseToken] mint failed — secure data session unavailable:', err?.message || err);
  if (!degraded) {
    degraded = true;
    emit(BRIDGE_DEGRADED_EVENT);
  }
  scheduleRetry();
}

function exitDegraded() {
  if (degraded) {
    degraded = false;
    emit(BRIDGE_RECOVERED_EVENT);
  }
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleRetry() {
  if (retryTimer || typeof setTimeout !== 'function') return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    // Background re-mint; success flips us out of degraded and pushes the
    // fresh token to realtime. Failure re-schedules via enterDegraded.
    getSupabaseAccessToken().catch(() => {});
  }, RETRY_INTERVAL_MS);
}

async function mintToken() {
  const [{ httpsCallable }, { functionsUsEast1 }] = await Promise.all([
    import('firebase/functions'),
    import('./init-firebase'),
  ]);
  const mint = httpsCallable(functionsUsEast1, 'getSupabaseToken');
  const res = await mint();
  return {
    token: res.data?.token || null,
    expiresAt: res.data?.expiresAt || 0,
  };
}

/**
 * Returns a valid bridge JWT, the cached one while still valid, or — when the
 * user is signed in and no token can be obtained — BRIDGE_DEGRADED_SENTINEL
 * (queries fail loudly with 401 rather than silently emptying the book).
 * Returns null only when signed out. Single-flight: concurrent callers share
 * one mint request.
 */
export async function getSupabaseAccessToken() {
  try {
    const { auth } = await import('./init-firebase');
    if (!auth?.currentUser) {
      cached = { token: null, expiresAt: 0 };
      exitDegraded();
      return null;
    }

    if (cached.token && Date.now() < cached.expiresAt - REFRESH_MARGIN_MS) {
      return cached.token;
    }

    // Failure cooldown: within MINT_COOLDOWN_MS of a failed mint, don't
    // re-attempt on every query — serve the still-valid cached token if any,
    // otherwise stay visibly degraded.
    if (Date.now() - lastFailureAt < MINT_COOLDOWN_MS && !inflight) {
      if (cached.token && Date.now() < cached.expiresAt) return cached.token;
      return BRIDGE_DEGRADED_SENTINEL;
    }

    if (!inflight) {
      inflight = (async () => {
        let result;
        try {
          result = await mintToken();
        } catch (firstErr) {
          // One quick in-line retry for transient blips before degrading.
          await new Promise((r) => setTimeout(r, 1000));
          result = await mintToken();
        }
        cached = result;

        // Realtime sockets authenticate at connect time — push the fresh
        // token so channel subscriptions survive the hardening flip.
        if (cached.token) {
          try {
            const { supabase } = await import('./supabase');
            supabase?.realtime?.setAuth(cached.token);
          } catch {
            /* realtime not ready yet — the accessToken option covers it */
          }
        }
        return cached.token;
      })().finally(() => {
        inflight = null;
      });
    }

    const token = await inflight;
    if (token) {
      exitDegraded();
      return token;
    }
    // Callable answered but without a token (e.g. failed-precondition —
    // secret unset). Treat as degraded rather than silently going anon.
    enterDegraded(new Error('getSupabaseToken returned no token'));
    return BRIDGE_DEGRADED_SENTINEL;
  } catch (err) {
    if (cached.token && Date.now() < cached.expiresAt) {
      // Mint failed but the previous token is still live — keep working and
      // let the background loop repair the session before it expires.
      lastFailureAt = Date.now();
      scheduleRetry();
      return cached.token;
    }
    enterDegraded(err);
    return BRIDGE_DEGRADED_SENTINEL;
  }
}

/** True while the bridge cannot obtain a token for a signed-in user. */
export function isBridgeDegraded() {
  return degraded;
}

/** Test hook / sign-out hook. */
export function _resetSupabaseTokenCache() {
  cached = { token: null, expiresAt: 0 };
  inflight = null;
  degraded = false;
  lastFailureAt = 0;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}
